import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, TrashIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import { ranges } from "@blis/shared/schemas/test-result-spec";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { mutationResponse } from "@/utils/trpc";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

type TestResultSpec = RouterOutputs["admin"]["testResultSpec"]["get"];
type RangeSpec =
  RouterOutputs["admin"]["testResultSpec"]["ranges"]["list"][number];

export function ResultRangesTable({
  resultSpec,
}: {
  resultSpec: NonNullable<TestResultSpec>;
}) {
  const { data } = trpc.admin.testResultSpec.ranges.list.useQuery({
    testResultSpecId: resultSpec.id,
  });

  const { options } = useAdminGridOptions(
    data,
    [
      {
        headerName: "Range",
        cellRenderer: (info) => {
          const row = info.data;
          if (row.freeText || row.textResult?.description) {
            return row.freeText || row.textResult?.description;
          }
          return `[${row.rangeLow || ""}-${row.rangeHigh || ""}]`;
        },
        cellRendererParams: (info) => ({
          data: info.data,
        }),
      },
      { field: "isReference" },
      {
        field: "sex",
        headerName: "Sex",
        cellRenderer: (info) => info.value || "-",
      },
      {
        headerName: "Age",
        cellRenderer: (info) => {
          const { ageLow, ageHigh } = info.data;
          if (!ageLow && !ageHigh) {
            return "-";
          }
          return `[${ageLow || ""}-${ageHigh || ""}]`;
        },
        cellRendererParams: (info) => ({ data: info.data }),
      },
      {
        headerName: "Actions",
        cellRenderer: (info) => (
          <div className="flex h-full items-center gap-1">
            <ResultRangeFormDialog
              resultSpec={resultSpec}
              rangeSpec={info.data}
            >
              <Button size="icon-sm" variant="ghost">
                <PencilIcon />
              </Button>
            </ResultRangeFormDialog>
            <RemoveResultRangeDialog rangeSpec={info.data}>
              <Button
                size="icon-sm"
                variant="ghost"
                className="text-destructive"
              >
                <TrashIcon />
              </Button>
            </RemoveResultRangeDialog>
          </div>
        ),
        cellRendererParams: (info) => ({ data: info.data }),
      },
    ],
    { sideBar: undefined },
  );

  return (
    <TabLayout
      description="Configure reference ranges and/or numeric ranges for the test result spec"
      actions={
        <ResultRangeFormDialog resultSpec={resultSpec}>
          <Button
            variant="secondary"
            isDisabled={resultSpec.type === "TEXT" && (data || []).length >= 1}
          >
            Add range spec
          </Button>
        </ResultRangeFormDialog>
      }
    >
      <div className="flex h-96">
        <AdminGrid {...options} />
      </div>
    </TabLayout>
  );
}

type RangeFormValues = z.infer<typeof ranges.create>;

function ResultRangeFormDialog({
  children,
  rangeSpec,
  resultSpec,
}: {
  children: ReactNode;
  rangeSpec?: RangeSpec;
  resultSpec: {
    id: number;
    type: "NUMERIC" | "TEXT" | "HLA" | "FREE_TEXT" | "ORGANISM";
  };
}) {
  const [open, setOpen] = useState(false);

  const methods = useForm({
    defaultValues: {
      ...rangeSpec,
      rangeLow: rangeSpec?.rangeLow?.toNumber(),
      rangeHigh: rangeSpec?.rangeHigh?.toNumber(),
      testResultSpecId: resultSpec.id,
    },
    resolver: zodResolver(ranges.create),
  });

  const create = trpc.admin.testResultSpec.ranges.create.useMutation();
  const update = trpc.admin.testResultSpec.ranges.update.useMutation();

  const onSubmit: SubmitHandler<RangeFormValues> = async (values) => {
    let p: Promise<unknown>;
    if (rangeSpec) {
      p = update.mutateAsync(
        { id: rangeSpec.id, ...values },
        mutationResponse({
          errorTitle: "Error updating range spec",
          successMessage: "Range spec updated",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    } else {
      p = create.mutateAsync(
        values,
        mutationResponse({
          errorTitle: "Error adding range spec",
          successMessage: "Range spec added",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    }
    await p;
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>{rangeSpec ? "Edit" : "Add"} range spec</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form onSubmit={methods.handleSubmit(onSubmit)} id="add-range-form">
              <FormProvider {...methods}>
                <input
                  type="hidden"
                  {...methods.register("testResultSpecId", {
                    valueAsNumber: true,
                  })}
                  value={resultSpec.id}
                />
                {resultSpec.type === "NUMERIC" ? (
                  <NumericResultRangeForm />
                ) : (
                  <TextResultRangeForm />
                )}
              </FormProvider>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-range-form"
              isPending={create.isPending || update.isPending}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function NumericResultRangeForm() {
  const {
    control,
    formState: { errors },
  } = useFormContext<RangeFormValues>();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Controller
          control={control}
          name="rangeLow"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value?.toString() ?? ""}
              type="number"
              id="range-low"
              validationBehavior="aria"
              isInvalid={!!error}
            >
              <Label>Result range</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <div style={{ marginTop: 28 }}>-</div>
        <Controller
          control={control}
          name="rangeHigh"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value?.toString() ?? ""}
              type="number"
              id="range-high"
              validationBehavior="aria"
              isInvalid={!!error}
            >
              <Label />
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>

      <div className="flex gap-2">
        <Controller
          control={control}
          name="ageLow"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value?.toString() ?? ""}
              type="number"
              id="age-low"
              validationBehavior="aria"
              isInvalid={!!error}
            >
              <Label>Age range</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <div style={{ marginTop: 28 }}>-</div>
        <Controller
          control={control}
          name="ageHigh"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value?.toString() ?? ""}
              type="number"
              id="age-high"
              validationBehavior="aria"
              isInvalid={!!error}
            >
              <Label />
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
      <Controller
        control={control}
        name="sex"
        render={({ field }) => (
          <RadioGroup
            id="sex-radio"
            value={field.value || ""}
            onChange={(val) => field.onChange(val || null)}
            orientation="horizontal"
          >
            <Label>Sex</Label>
            <Radio value="" id="sex-unknown">
              None
            </Radio>
            <Radio value={"M"} id="sex-m">
              Male
            </Radio>
            <Radio value={"F"} id="sex-f">
              Female
            </Radio>
          </RadioGroup>
        )}
      />
      <Controller
        control={control}
        name="isReference"
        render={({ field }) => (
          <CheckboxField>
            <Checkbox
              id="check-is-reference"
              isSelected={field.value}
              onChange={(flag) => field.onChange(!!flag)}
              onBlur={field.onBlur}
              name={field.name}
            >
              Is reference
            </Checkbox>
            <Description>
              This range spec is considered a reference range
            </Description>
          </CheckboxField>
        )}
      />
    </div>
  );
}

function TextResultRangeForm() {
  const { data, isLoading } = trpc.admin.testResult.list.useQuery({
    category: "TEXT",
  });

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<RangeFormValues>();

  const [freeText, textResultId] = watch(["freeText", "textResultId"]);

  return (
    <>
      <input
        type="hidden"
        {...register("isReference", { setValueAs: () => true })}
        value="true"
      />
      <Tabs
        variant="segment"
        defaultSelectedKey={textResultId ? "textResult" : "freeText"}
      >
        <TabsList className="mb-4">
          <Tab id="textResult">Test result</Tab>
          <Tab id="freeText">Custom value</Tab>
        </TabsList>
        <TabPanel id="textResult">
          <ComboBox
            selectedKey={textResultId}
            onSelectionChange={(key) => {
              setValue("textResultId", key?.toString());
              setValue("freeText", null);
            }}
            isInvalid={!!errors.textResultId}
          >
            <Label>Reference range</Label>
            <ComboBoxInput />
            <ComboBoxList items={data || []}>
              {(item) => (
                <ComboBoxItem id={item.value} textValue={item.description}>
                  {item.description}
                </ComboBoxItem>
              )}
            </ComboBoxList>
            <FieldError>{errors.textResultId?.message}</FieldError>
          </ComboBox>
        </TabPanel>
        <TabPanel id="freeText">
          <TextField
            {...register("freeText")}
            value={freeText || ""}
            onChange={(value) => {
              setValue("freeText", value);
              setValue("textResultId", null);
            }}
            isInvalid={!!errors.freeText}
          >
            <Label>Custom reference range</Label>
            <Input />
            <FieldError>{errors.freeText?.message}</FieldError>
          </TextField>
        </TabPanel>
      </Tabs>
    </>
  );
}

function RemoveResultRangeDialog({
  rangeSpec,
  children,
}: {
  children: ReactNode;
  rangeSpec: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteSpec = trpc.admin.testResultSpec.ranges.delete.useMutation();

  const onConfirm = () => {
    deleteSpec.mutate(
      {
        id: rangeSpec.id,
      },
      mutationResponse({
        errorTitle: "Error removing range spec",
        successMessage: "Range spec removed",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };
  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      danger
      title="Remove range spec"
      message="Are you sure you want to remove this range spec?"
      onConfirm={onConfirm}
      loading={deleteSpec.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
